import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ReactComponent as NotificationIcPriorityHigh24px } from '../../../assets/icons/notification/ic_priority_high_24px.svg';
import { ReactComponent as NavigationIcCheck24px } from '../../../assets/icons/navigation/ic_check_24px.svg';

const radioButtonSvg = (
  <svg
    className="motif-progress-indicator-icon-svg"
    fill="currentColor"
    preserveAspectRatio="xMidYMid meet"
    height="24px"
    width="24px"
    viewBox="0 0 24 24"
  >
    <circle
      className="radio-outline"
      cx="12"
      cy="12"
      r="11"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
    />
    <circle className="radio-dot-active" cx="12" cy="12" r="7" fill="currentColor" />
    <circle className="radio-dot-hover" cx="12" cy="12" r="5" fill="currentColor" />
  </svg>
);

const defaultIcons = {
  complete: <NavigationIcCheck24px />,
  error: <NotificationIcPriorityHigh24px />,
  incomplete: radioButtonSvg
};

/**
 * @typedef Props
 * @prop {string} [className]
 * @prop {string} [heading]
 * @prop {string} [body]
 * @prop {'active' | 'completed' | 'default' | 'warning'} [status]
 * @prop {boolean} [error]
 * @prop {boolean} [disabled]
 * @prop {() => void} [onClick]
 * @prop {{complete: React.ReactNode, error: React.ReactNode, incomplete: React.ReactNode}} [icons]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifProgressIndicatorStep = (props) => {
  const {
    className = '',
    heading = '',
    body = '',
    status = 'default',
    error = false,
    disabled = false,
    onClick = null,
    icons: userIcons = {},
    ...other
  } = props;
  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  const classes = classNames({
    'motif-progress-indicator-step': true,
    [`motif-progress-indicator-step-${status}`]: !error && !disabled && status && status.length > 0,
    'motif-progress-indicator-step-error': error,
    'motif-progress-indicator-step-disabled': disabled,
    [className]: true
  });

  const getStatusA11y = (() => `status: ${(error && 'error') || (disabled && 'disable') || status}.`)();

  const handleKeyPress = (ev) => {
    if (ev.keyCode === 13 || ev.keyCode === 32) {
      onClick();
    }
  };

  return (
    <div
      {...other}
      aria-checked={status === 'active'}
      role={status === 'active' ? 'checkbox' : 'button'}
      className={classes}
      disabled={disabled}
      data-motif-progress-indicator-step
      onKeyDown={handleKeyPress}
      tabIndex={-1}
    >
      <button className="motif-progress-indicator-text-lines" onClick={onClick}>
        {heading && <p className="motif-progress-indicator-heading">{heading}</p>}
        {body && (
          <React.Fragment>
            <p aria-label={body} className="motif-progress-indicator-body">
              {body}
            </p>
            <span style={{ color: 'transparent', fontSize: '0' }}>{getStatusA11y}</span>
          </React.Fragment>
        )}
      </button>
      <div className="motif-progress-indicator-icon">
        {error || status === 'warning' ? (
          icons.error
        ) : (
          icons.complete
        )}
        {icons.incomplete}
      </div>
    </div>
  );
};

MotifProgressIndicatorStep.propTypes = {
  className: PropTypes.string,
  heading: PropTypes.string,
  body: PropTypes.string,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  status: PropTypes.oneOf(['active', 'completed', 'default', 'warning']),
  onClick: PropTypes.func,
  icons: PropTypes.shape({
    complete: PropTypes.node,
    error: PropTypes.node,
    incomplete: PropTypes.node
  })
};

export { MotifProgressIndicatorStep };
